import { z } from "zod";

export const dnsRecordSchema = z.object({
  type: z.string().describe("DNS record type (A, AAAA, CNAME, TXT)"),
  host: z.string().describe("DNS record host"),
  value: z.string().describe("DNS record value to set"),
});

const rootDomainDnsInstructionsSchema = z.object({
  type: z.literal("root"),
  records: z.array(dnsRecordSchema),
  verificationToken: z.string().nullable(),
  verificationTokenExpiresAt: z.string().nullable(),
});

const subdomainDnsInstructionsSchema = z.object({
  type: z.literal("subdomain"),
  records: z.array(dnsRecordSchema),
});

export const dnsInstructionsSchema = z.discriminatedUnion("type", [
  rootDomainDnsInstructionsSchema,
  subdomainDnsInstructionsSchema,
]);

// Only the output schemas live here: their generated types are consumed by the
// bedrock SDK, which must not import from MCP-specific code. The matching input
// schemas are defined in `schemas/agentTools/replo/bedrockMcp` since they're
// only used there (to register the MCP tools).

export const addCustomDomainOutputSchema = z
  .object({
    customDomainId: z.string(),
    domain: z.string(),
    isPending: z.boolean(),
    isStale: z.boolean(),
    dnsInstructions: dnsInstructionsSchema,
  })
  .describe("AddCustomDomainOutput");

export const findCustomDomainsOutputSchema = z
  .object({
    domains: z.array(
      z.object({
        id: z.string(),
        value: z.string(),
        isPending: z.boolean(),
        isStale: z.boolean(),
        isPrimary: z
          .boolean()
          .describe("Whether this is the site's primary domain"),
        dnsInstructions: dnsInstructionsSchema,
      }),
    ),
    siteSubdomain: z.string().nullable(),
  })
  .describe("FindCustomDomainsOutput");

export const findDomainsResponseSchema = z
  .object({
    domains: z.array(
      z.object({
        id: z.string(),
        value: z.string(),
        isPending: z.boolean(),
        isStale: z.boolean(),
        isPrimary: z
          .boolean()
          .describe("Whether this is the site's primary domain"),
        dnsInstructions: dnsInstructionsSchema,
      }),
    ),
    siteSubdomain: z.string(),
  })
  .describe("FindDomainsResponse");

export const getCustomDomainOutputSchema = z
  .object({
    customDomainId: z.string(),
    domain: z.string(),
    isPending: z.boolean(),
    isStale: z.boolean(),
    dnsInstructions: dnsInstructionsSchema,
  })
  .describe("GetCustomDomainOutput");

export const deleteCustomDomainOutputSchema = z
  .object({
    deleted: z.boolean(),
  })
  .describe("DeleteCustomDomainOutput");

export const verifyCustomDomainOutputSchema = z
  .object({
    customDomainId: z.string(),
    domain: z.string(),
    isActive: z.boolean(),
    cloudflareCustomHostnameId: z.string(),
  })
  .describe("VerifyCustomDomainOutput");

export const setPrimaryCustomDomainOutputSchema = z
  .object({
    customDomainId: z.string(),
    domain: z.string(),
    isPrimary: z.boolean(),
  })
  .describe("SetPrimaryCustomDomainOutput");
